/**
 * AdminAudioResync — admin-only "re-sync phase markers" panel.
 *
 * Why: every audio re-recording was forcing a code edit + deploy to update
 * phase markers (the `{start,text}` pairs that show the right line at the
 * right second). Now the admin clicks a single button per track, the backend
 * runs Whisper, and the synced markers are persisted to MongoDB. Frontend
 * players check `/api/audio/phases/<track_id>` on mount and prefer the
 * override when present — so a re-recording auto-realigns with zero code.
 *
 * Route: /admin/audio/resync
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import { ArrowLeft, RotateCw, Check, Trash2, Loader2, Mic, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

// The catalogue of tracks we know how to re-sync. Adding a new one is just
// dropping a row here — no backend change needed.
const RESYNC_TARGETS = [
  // Aurum Edge — Deep Work
  { track_id: "aurum_alignment",        audio_path: "static/audio/voice/aurum/aurum_alignment.m4a",        label: "Aurum Alignment",         group: "Aurum Deep Work" },
  { track_id: "wealth_normalisation",   audio_path: "static/audio/voice/aurum/aurum_wealth_normalisation.m4a", label: "Wealth Normalisation", group: "Aurum Deep Work" },
  { track_id: "future_team",            audio_path: "static/audio/voice/aurum/aurum_future_team.m4a",     label: "Future Team",             group: "Aurum Deep Work" },
  { track_id: "identity_loop",          audio_path: "static/audio/voice/aurum/aurum_identity_loop.m4a",   label: "Identity Loop",           group: "Aurum Deep Work" },
  // The Edge — Performance Resets
  { track_id: "pre_call",               audio_path: "static/audio/voice/edge/reset_pre_call.m4a",         label: "Pre-Call Reset",          group: "Performance Resets" },
  { track_id: "rejection",              audio_path: "static/audio/voice/edge/reset_rejection.m4a",        label: "Rejection Reset",         group: "Performance Resets" },
  { track_id: "confidence",             audio_path: "static/audio/voice/edge/reset_confidence.m4a",       label: "Confidence Reset",        group: "Performance Resets" },
];

export default function AdminAudioResync() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [synced, setSynced] = useState({});  // track_id → metadata
  const [busy, setBusy] = useState({});      // track_id → bool
  const [errors, setErrors] = useState({});  // track_id → string

  const refresh = async () => {
    try {
      const r = await fetch(`${API}/api/admin/audio/synced-tracks`, { headers: auth() });
      if (!r.ok) return;
      const d = await r.json();
      const map = {};
      for (const t of (d.tracks || [])) map[t.track_id] = t;
      setSynced(map);
    } catch (_) { /* noop */ }
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  const resync = async (target) => {
    setErrors((prev) => ({ ...prev, [target.track_id]: null }));
    setBusy((prev) => ({ ...prev, [target.track_id]: true }));
    try {
      const r = await fetch(`${API}/api/admin/audio/resync`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ track_id: target.track_id, audio_path: target.audio_path }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} · ${t}`);
      }
      await r.json();
      await refresh();
    } catch (e) {
      setErrors((prev) => ({ ...prev, [target.track_id]: String(e.message || e) }));
    } finally {
      setBusy((prev) => ({ ...prev, [target.track_id]: false }));
    }
  };

  const revert = async (target) => {
    setBusy((prev) => ({ ...prev, [target.track_id]: true }));
    try {
      const r = await fetch(`${API}/api/admin/audio/resync/${encodeURIComponent(target.track_id)}`, {
        method: "DELETE",
        headers: auth(),
      });
      if (r.ok) await refresh();
    } finally {
      setBusy((prev) => ({ ...prev, [target.track_id]: false }));
    }
  };

  if (user && user.role !== "admin") {
    navigate("/home");
    return null;
  }

  const groups = Array.from(new Set(RESYNC_TARGETS.map((t) => t.group)));

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto py-10 px-6">
        <button
          onClick={() => navigate("/admin")}
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"
          data-testid="audio-resync-back-btn"
        >
          <ArrowLeft size={14} /> Back to Admin
        </button>

        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white">
            <Mic size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-medium text-slate-900">Audio · Re-sync Phase Markers</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Re-record a voice take, click Re-sync, done. Whisper will transcribe the audio
              and update the phase markers so every line lands at the exact spoken moment —
              no code deploy, no drift.
            </p>
          </div>
        </div>

        {groups.map((group) => (
          <section key={group} className="mb-10">
            <h2 className="text-[11px] tracking-[0.32em] uppercase text-slate-400 mb-4">
              {group}
            </h2>
            <div className="space-y-3">
              {RESYNC_TARGETS.filter((t) => t.group === group).map((t) => {
                const meta = synced[t.track_id];
                const isBusy = busy[t.track_id];
                const err = errors[t.track_id];
                return (
                  <div
                    key={t.track_id}
                    className="rounded-2xl border border-slate-200 bg-white px-5 py-4 flex items-center justify-between gap-4"
                    data-testid={`resync-row-${t.track_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-900 font-medium">{t.label}</p>
                      <p className="text-xs text-slate-500 truncate font-mono">
                        {t.audio_path}
                      </p>
                      {meta ? (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <Check size={12} />
                          {meta.phrase_count} markers · {meta.duration ? `${Math.round(meta.duration)}s` : "—"} · synced {timeAgo(meta.updated_at)}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 mt-1">No override · using hardcoded markers</p>
                      )}
                      {err && (
                        <p className="text-xs text-rose-600 mt-1 flex items-center gap-1">
                          <AlertTriangle size={12} /> {err}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {meta && (
                        <button
                          onClick={() => revert(t)}
                          disabled={isBusy}
                          className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1 px-3 py-2 rounded-full border border-slate-200 hover:border-rose-200 transition-colors disabled:opacity-50"
                          data-testid={`resync-revert-${t.track_id}`}
                          title="Remove override (revert to hardcoded markers)"
                        >
                          <Trash2 size={12} /> Revert
                        </button>
                      )}
                      <button
                        onClick={() => resync(t)}
                        disabled={isBusy}
                        className="text-xs text-white bg-slate-900 hover:bg-slate-800 inline-flex items-center gap-1.5 px-4 py-2 rounded-full transition-colors disabled:opacity-60"
                        data-testid={`resync-run-${t.track_id}`}
                      >
                        {isBusy ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                        {isBusy ? "Syncing…" : meta ? "Re-sync" : "Sync"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}

        <p className="mt-10 text-xs text-slate-400 leading-relaxed">
          Each sync takes 8–15 seconds — Whisper transcribes the file via the Emergent LLM key
          and the resulting <code className="font-mono text-slate-500">{`{start, text}`}</code> markers
          are written to a Mongo override that the live players check on mount. To revert to the
          hardcoded fallback, click Revert.
        </p>
      </div>
    </AdminLayout>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}
